import { useMutation } from "@apollo/client";
import CopyAllOutlined from "@mui/icons-material/CopyAllOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ThumbDownAlt from "@mui/icons-material/ThumbDownAlt";
import ThumbDownAltOutlined from "@mui/icons-material/ThumbDownAltOutlined";
import ThumbUpAlt from "@mui/icons-material/ThumbUpAlt";
import ThumbUpAltOutlined from "@mui/icons-material/ThumbUpAltOutlined";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Stack } from "@mui/system";
import { Dialog } from "components/Dialog";
import ScoutIcon from "components/ScoutIcon";
import DOMPurify from "dompurify";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { gql } from "generated-graphql";
import { ScoutMessageFeedbackRating } from "generated-graphql/graphql";
import { useCopyToClipboard } from "hooks/useCopyToClipboard";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { marked } from "marked";
import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type MouseEvent,
  type ReactNode,
} from "react";
import { useParams } from "react-router-dom";

import type { StreamStatus } from "../../../../../stream";
import type { ScoutActiveTurn } from "../../hooks/types";

import {
  useScoutDeepLinkTargetId,
  useScoutDeepLinkUrl,
  useScoutIsDeepLinkTarget,
} from "../../ScoutDeepLinkContext";
import { ScoutDeepLinkCopyButton } from "../../ScoutDeepLinkCopyButton";
import { getScoutDeepLinkTargetSx } from "../../scoutDeepLinks";
import {
  isScoutEncampRepresentativeTool,
  parseEncampRepresentative,
} from "../scoutEncampRepresentative";
import { useScoutHostContext } from "../ScoutHostContext";
import { renderScoutProposalCardsFromToolParts } from "../scoutProposalRegistry";
import { isScoutProUpsellTool, parseScoutProUpsell } from "../scoutProUpsell";
import { ScoutProUpsellCard } from "../ScoutProUpsellCard";
import { ScoutRepresentativeInline } from "../ScoutRepresentativeInline";
import { ScoutTodoList } from "../ScoutTodoList";
import {
  deriveTodoSnapshotFromMessageParts,
  SCOUT_TODO_TOOL_NAMES,
  type ScoutTodoEpoch,
} from "../scoutTodoSnapshot";
import { ToolCallGroup } from "../ToolCallGroup";
import { MessageContent } from "./MessageContent";
import {
  getTurnErrorMessage,
  SCOUT_ASSISTANT_MESSAGE_ID_PREFIX,
  shouldSuppressAgentMessageToolbar,
} from "./scoutAgentTurnUi";
import {
  getScoutMessageText,
  getScoutTerminalError,
  isTerminalScoutErrorPart,
  ScoutChatMessage,
  ScoutChatMessagePart,
  ScoutReasoningPart,
  ScoutTextPart,
  ScoutToolPart,
} from "./scoutChatMessage";
import { useScoutMessageSpacing } from "./scoutMessageSpacing";

const RATE_SCOUT_MESSAGE = gql(`
  mutation RateScoutMessage($input: RateScoutMessageInput!) {
    rateScoutMessage(input: $input) {
      id
      rating
      createdAt
      updatedAt
    }
  }
`);

/**
 * A render block is the smallest unit the agent message renders. We collapse
 * runs of like-kinded parts into one block so the layout matches the agent's
 * actual flow: a stretch of tool calls renders as a single grouped widget,
 * and a stretch of text renders as one prose chunk.
 *
 * Reasoning parts are gated by the ScoutReasoningText feature flag. When the
 * flag is on, they render in the same subdued style as thinking text, not as
 * normal answer markdown. That preserves the chronological stream while keeping
 * final-answer markdown parsing isolated from provider thinking summaries.
 *
 * **Note on persistence.** Reasoning rows ARE persisted server-side as
 * `ScoutMessagePart` rows with `type='reasoning'` (see
 * `packages/encamp-scout-connection/src/scout/scout-conversation-materializer.ts`).
 * The hide-in-customer-UI policy is render-only. If reasoning content ever
 * has to be kept out of the database for compliance reasons (PII, leaked
 * mechanics that shouldn't survive logout, etc.), the right place to filter
 * is the materializer at write-time, not this file.
 *
 * Source / file / step parts are dropped here because the customer UI doesn't
 * surface them.
 */
type RenderTextPart = ScoutTextPart | ScoutReasoningPart;

type RenderBlock =
  | { kind: "text"; id: string; parts: RenderTextPart[] }
  | {
      kind: "tool-group";
      id: string;
      groupIndex: number;
      tools: ScoutToolPart[];
    };

const UUID_V4_OR_V5_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

type AgentMessageProps = {
  message: ScoutChatMessage;
  activeTurn?: ScoutActiveTurn;
  streamStatus?: StreamStatus;
  /**
   * Staff Observe debug surface: expands raw tool name / input / output / error.
   * Customer chat keeps this false so internals stay hidden.
   */
  showToolIoExpansion?: boolean;
  showStaffFeedback?: boolean;
  /**
   * Checklists hosted on this message (from conversation epochs). When provided
   * (including an empty array), overrides per-message snapshot derivation so a
   * resume can relocate an incomplete list without replacing finished ones.
   * Epoch definition + hosting state machine: `ScoutTodoEpoch` in
   * `../scoutTodoSnapshot`.
   */
  hostedTodoEpochs?: ScoutTodoEpoch[];
  /** When set, todo tool rows are hidden on every assistant message in the thread. */
  hideConversationTodoToolRows?: boolean;
};

const STAFF_FEEDBACK_PROMPT =
  "What is the correct answer? Be as specific as possible. This helps improve Scout.";

function resolveRateableMessageId(message: ScoutChatMessage): string | undefined {
  if (message.persistedMessageId && UUID_V4_OR_V5_REGEX.test(message.persistedMessageId)) {
    return message.persistedMessageId;
  }

  if (
    !message.id.startsWith(SCOUT_ASSISTANT_MESSAGE_ID_PREFIX) &&
    UUID_V4_OR_V5_REGEX.test(message.id)
  ) {
    return message.id;
  }

  return undefined;
}

function appendTextBlock(blocks: RenderBlock[], part: RenderTextPart) {
  const last = blocks[blocks.length - 1];
  if (last?.kind === "text") {
    last.parts.push(part);
  } else {
    blocks.push({ kind: "text", id: part.id, parts: [part] });
  }
}

function groupParts(parts: ScoutChatMessagePart[], showReasoningText: boolean): RenderBlock[] {
  const blocks: RenderBlock[] = [];
  let toolGroupIndex = 0;

  for (const part of parts) {
    const last = blocks[blocks.length - 1];

    if (part.type === "tool") {
      if (last?.kind === "tool-group") {
        last.tools.push(part);
      } else {
        blocks.push({
          kind: "tool-group",
          id: part.id,
          groupIndex: toolGroupIndex,
          tools: [part],
        });
        toolGroupIndex += 1;
      }
      continue;
    }

    if (part.type === "text") {
      if (isTerminalScoutErrorPart(part)) {
        continue;
      }

      appendTextBlock(blocks, part);
      continue;
    }

    if (part.type === "reasoning") {
      if (showReasoningText) {
        appendTextBlock(blocks, part);
      }
      continue;
    }

    // Source, file, step parts: intentionally not rendered. See RenderBlock
    // JSDoc for the rationale.
  }

  return blocks;
}

function findLocalChecklistAnchorToolCallId(parts: ScoutChatMessagePart[]): string | undefined {
  for (const part of parts) {
    if (part.type === "tool" && part.toolName === "create_todos") {
      return part.toolCallId;
    }
  }
  for (const part of parts) {
    if (
      part.type === "tool" &&
      SCOUT_TODO_TOOL_NAMES.has(part.toolName) &&
      part.toolName !== "get_todos"
    ) {
      return part.toolCallId;
    }
  }
  return undefined;
}

function sameHostedTodoEpochs(
  previous: ScoutTodoEpoch[] | undefined,
  next: ScoutTodoEpoch[] | undefined
): boolean {
  if (previous === next) {
    return true;
  }
  if (previous === undefined || next === undefined) {
    return false;
  }
  if (previous.length !== next.length) {
    return false;
  }
  for (let index = 0; index < previous.length; index += 1) {
    const left = previous[index];
    const right = next[index];
    if (!left || !right) {
      return false;
    }
    if (
      left.createMessageId !== right.createMessageId ||
      left.hostMessageId !== right.hostMessageId ||
      left.anchorToolCallId !== right.anchorToolCallId ||
      left.snapshot !== right.snapshot
    ) {
      return false;
    }
  }
  return true;
}

/**
 * Render a tool group, inserting hosted checklists immediately after their
 * anchor tool call so later research tools do not drag the list downward.
 * Split segments get distinct groupIndex values so expand/collapse state does
 * not bleed across the checklist.
 */
function renderToolGroupWithChecklists({
  block,
  message,
  toolGroupExpandedOverrides,
  handleToolGroupExpandedChange,
  showToolIoExpansion,
  hideTodoToolRows,
  targetToolCallId,
  epochsByAnchor,
}: {
  block: Extract<RenderBlock, { kind: "tool-group" }>;
  message: ScoutChatMessage;
  toolGroupExpandedOverrides: Record<number, boolean>;
  handleToolGroupExpandedChange: (groupIndex: number, nextExpanded: boolean) => void;
  showToolIoExpansion: boolean;
  hideTodoToolRows: boolean;
  targetToolCallId: string | undefined;
  epochsByAnchor: Map<string, ScoutTodoEpoch[]>;
}): ReactNode {
  const splitIndexes: number[] = [];
  for (let index = 0; index < block.tools.length; index += 1) {
    const tool = block.tools[index];
    if (tool && epochsByAnchor.has(tool.toolCallId)) {
      splitIndexes.push(index);
    }
  }

  // Remultiply so split ordinals never collide with later unsplit groups
  // (group 1 would otherwise share index 1 with group 0's second segment).
  const baseGroupIndex = block.groupIndex * 1000;

  if (splitIndexes.length === 0) {
    return renderBlock({
      block: {
        ...block,
        groupIndex: baseGroupIndex,
      },
      message,
      toolGroupExpandedOverrides,
      handleToolGroupExpandedChange,
      showToolIoExpansion,
      hideTodoToolRows,
      targetToolCallId,
    });
  }

  const segments: ReactNode[] = [];
  let start = 0;
  let segmentOrdinal = 0;
  for (const splitIndex of splitIndexes) {
    const headTools = block.tools.slice(start, splitIndex + 1);
    if (headTools.length > 0) {
      const headTool = headTools[0];
      if (!headTool) {
        continue;
      }
      const segmentGroupIndex = baseGroupIndex + segmentOrdinal;
      segmentOrdinal += 1;
      segments.push(
        renderBlock({
          block: {
            kind: "tool-group",
            id: `${block.id}-seg-${start}`,
            groupIndex: segmentGroupIndex,
            tools: headTools,
          },
          message,
          toolGroupExpandedOverrides,
          handleToolGroupExpandedChange,
          showToolIoExpansion,
          hideTodoToolRows,
          targetToolCallId,
        })
      );
    }

    const anchorTool = block.tools[splitIndex];
    const anchoredEpochs = anchorTool ? epochsByAnchor.get(anchorTool.toolCallId) : undefined;
    if (anchoredEpochs) {
      for (const epoch of anchoredEpochs) {
        segments.push(
          <ScoutTodoList
            key={`todo-${epoch.anchorToolCallId}-${epoch.createMessageId}`}
            snapshot={epoch.snapshot}
            isStreaming={message.isStreaming}
          />
        );
      }
    }
    start = splitIndex + 1;
  }

  const tailTools = block.tools.slice(start);
  if (tailTools.length > 0) {
    const tailTool = tailTools[0];
    if (tailTool) {
      const segmentGroupIndex = baseGroupIndex + segmentOrdinal;
      segments.push(
        renderBlock({
          block: {
            kind: "tool-group",
            id: `${block.id}-seg-${start}`,
            groupIndex: segmentGroupIndex,
            tools: tailTools,
          },
          message,
          toolGroupExpandedOverrides,
          handleToolGroupExpandedChange,
          showToolIoExpansion,
          hideTodoToolRows,
          targetToolCallId,
        })
      );
    }
  }

  return <Fragment key={block.id}>{segments}</Fragment>;
}

function renderBlock({
  block,
  message,
  toolGroupExpandedOverrides,
  handleToolGroupExpandedChange,
  showToolIoExpansion,
  hideTodoToolRows,
  targetToolCallId,
}: {
  block: RenderBlock;
  message: ScoutChatMessage;
  toolGroupExpandedOverrides: Record<number, boolean>;
  handleToolGroupExpandedChange: (groupIndex: number, nextExpanded: boolean) => void;
  showToolIoExpansion: boolean;
  hideTodoToolRows: boolean;
  targetToolCallId: string | undefined;
}): ReactNode {
  if (block.kind === "tool-group") {
    const proposalCards = renderScoutProposalCardsFromToolParts(block.tools);

    const upsellCards = block.tools
      .filter(isScoutProUpsellTool)
      .map((toolPart) => {
        const upsell = parseScoutProUpsell(toolPart.toolOutput);
        if (!upsell) return null;
        return <ScoutProUpsellCard key={`${toolPart.id}-upsell-card`} upsell={upsell} />;
      })
      .filter((node): node is JSX.Element => node !== null);

    const representativeCards = block.tools
      .filter(isScoutEncampRepresentativeTool)
      .map((toolPart) => {
        const representative = parseEncampRepresentative(toolPart.toolOutput);
        if (!representative) return null;
        return <ScoutRepresentativeInline key={`${toolPart.id}-representative-card`} />;
      })
      .filter((node): node is JSX.Element => node !== null);

    const containsDeepLinkTarget =
      targetToolCallId !== undefined &&
      block.tools.some((tool) => tool.toolCallId === targetToolCallId);

    return (
      <Fragment key={block.id}>
        <ToolCallGroup
          toolParts={block.tools}
          isStreaming={message.isStreaming}
          expanded={toolGroupExpandedOverrides[block.groupIndex] ?? containsDeepLinkTarget}
          onExpandedChange={(nextExpanded) =>
            handleToolGroupExpandedChange(block.groupIndex, nextExpanded)
          }
          showToolIoExpansion={showToolIoExpansion}
          hideTodoToolRows={hideTodoToolRows}
        />
        {proposalCards}
        {upsellCards}
        {representativeCards}
      </Fragment>
    );
  }

  return (
    <MessageContent
      key={block.id}
      messageId={`${message.id}-${block.id}`}
      parts={block.parts}
      isStreaming={message.isStreaming}
    />
  );
}

export const AgentMessage = memo(
  function AgentMessage({
    message,
    activeTurn,
    streamStatus,
    showToolIoExpansion = false,
    showStaffFeedback = false,
    hostedTodoEpochs,
    hideConversationTodoToolRows = false,
  }: AgentMessageProps) {
    const stableMessageLinkId = message.persistedMessageId;
    const { featureFlags } = useFeatureFlags();
    const showReasoningText = Boolean(featureFlags[FeatureFlagKey.ScoutReasoningText]);
    const terminalError = useMemo(() => getScoutTerminalError(message), [message]);
    const terminalErrorParts = useMemo<ScoutTextPart[]>(() => {
      if (!terminalError) {
        return [];
      }

      return [
        {
          id: `${message.id}-terminal-error-message`,
          type: "text",
          text: getTurnErrorMessage({ code: terminalError }),
          agentState: "error",
        },
      ];
    }, [message.id, terminalError]);
    const blocks = useMemo(
      () => groupParts(message.parts, showReasoningText),
      [message.parts, showReasoningText]
    );
    const messageAnchorId = stableMessageLinkId ?? message.id;
    const isDeepLinkTarget = useScoutIsDeepLinkTarget(stableMessageLinkId);
    const targetToolCallId = useScoutDeepLinkTargetId();
    const { rowPaddingX, assistantContentPaddingX, assistantContentMaxWidth } =
      useScoutMessageSpacing();
    const resolvedHostedTodoEpochs = useMemo((): ScoutTodoEpoch[] => {
      if (hostedTodoEpochs !== undefined) {
        return hostedTodoEpochs;
      }

      const snapshot = deriveTodoSnapshotFromMessageParts(message.parts, {
        isStreaming: message.isStreaming,
      });
      if (!snapshot?.hasChecklist) {
        return [];
      }

      const anchorToolCallId = findLocalChecklistAnchorToolCallId(message.parts);
      if (!anchorToolCallId) {
        return [];
      }

      return [
        {
          snapshot,
          createMessageId: message.id,
          hostMessageId: message.id,
          anchorToolCallId,
        },
      ];
    }, [hostedTodoEpochs, message.id, message.parts, message.isStreaming]);
    const epochsByAnchor = useMemo(() => {
      const map = new Map<string, ScoutTodoEpoch[]>();
      for (const epoch of resolvedHostedTodoEpochs) {
        const existing = map.get(epoch.anchorToolCallId);
        if (existing) {
          existing.push(epoch);
        } else {
          map.set(epoch.anchorToolCallId, [epoch]);
        }
      }
      return map;
    }, [resolvedHostedTodoEpochs]);
    const hideTodoToolRows =
      hostedTodoEpochs !== undefined
        ? hideConversationTodoToolRows
        : resolvedHostedTodoEpochs.length > 0;
    /**
     * Stores the user's explicit expand/collapse choices by remapped tool-group
     * index. Split segments around a checklist get distinct indexes so expand
     * state does not bleed across the checklist. Indexes are based on group
     * position (not part ids) so expand state survives stream → persist
     * rehydration. If a group has no entry here, its default expanded state
     * comes from whether it contains the active deep-linked tool call.
     */
    const [toolGroupExpandedOverrides, setToolGroupExpandedOverrides] = useState<
      Record<number, boolean>
    >({});

    const handleToolGroupExpandedChange = useCallback(
      (groupIndex: number, nextExpanded: boolean) => {
        setToolGroupExpandedOverrides((current) => {
          if (current[groupIndex] === nextExpanded) {
            return current;
          }
          return { ...current, [groupIndex]: nextExpanded };
        });
      },
      []
    );

    let messageContent: ReactNode;
    if (terminalError) {
      messageContent = (
        <MessageContent
          messageId={`${message.id}-terminal-error`}
          parts={terminalErrorParts}
          isStreaming={false}
        />
      );
    } else {
      const renderedAnchors = new Set<string>();
      messageContent = (
        <>
          {blocks.map((block) => {
            if (block.kind === "tool-group") {
              for (const tool of block.tools) {
                if (epochsByAnchor.has(tool.toolCallId)) {
                  renderedAnchors.add(tool.toolCallId);
                }
              }
              return renderToolGroupWithChecklists({
                block,
                message,
                toolGroupExpandedOverrides,
                handleToolGroupExpandedChange,
                showToolIoExpansion,
                hideTodoToolRows,
                targetToolCallId,
                epochsByAnchor,
              });
            }

            return renderBlock({
              block,
              message,
              toolGroupExpandedOverrides,
              handleToolGroupExpandedChange,
              showToolIoExpansion,
              hideTodoToolRows,
              targetToolCallId,
            });
          })}
          {resolvedHostedTodoEpochs
            .filter((epoch) => !renderedAnchors.has(epoch.anchorToolCallId))
            .map((epoch) => (
              <ScoutTodoList
                key={`todo-fallback-${epoch.anchorToolCallId}-${epoch.createMessageId}`}
                snapshot={epoch.snapshot}
                isStreaming={message.isStreaming}
              />
            ))}
        </>
      );
    }

    return (
      <Box
        id={messageAnchorId}
        // Allow deep-link targets to receive programmatic focus without adding a tab stop.
        tabIndex={-1}
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          py: 1.75,
          px: rowPaddingX,
          ...getScoutDeepLinkTargetSx(isDeepLinkTarget),
        }}
      >
        <Stack
          direction="row"
          justifyContent="flex-start"
          alignItems="flex-start"
          spacing={1}
          sx={{
            maxWidth: assistantContentMaxWidth,
            px: assistantContentPaddingX,
            py: 1.5,
            borderRadius: 3,
            bgcolor: "transparent",
            color: "text.primary",
            overflowWrap: "break-word",
          }}
        >
          <Box
            sx={{
              "@keyframes scoutSpin": {
                from: { transform: "rotate(0deg)" },
                to: { transform: "rotate(360deg)" },
              },
            }}
          >
            <ScoutIcon
              sx={{
                color: "#B0BEC5",
                animation: message.isStreaming ? "scoutSpin 1.1s linear infinite" : "none",
                transformOrigin: "center",
                mt: 0.125,
              }}
            />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack spacing={2.25}>{messageContent}</Stack>
            {!terminalError && (
              <>
                <Box sx={{ height: 10 }} />
                <AgentMessageToolbar
                  message={message}
                  activeTurn={activeTurn}
                  streamStatus={streamStatus}
                />
                <StaffFeedbackAccordion message={message} showStaffFeedback={showStaffFeedback} />
              </>
            )}
          </Box>
        </Stack>
      </Box>
    );
  },
  (prev, next) =>
    prev.message.id === next.message.id &&
    prev.message.isStreaming === next.message.isStreaming &&
    prev.message.parts === next.message.parts &&
    prev.message.feedback === next.message.feedback &&
    prev.activeTurn === next.activeTurn &&
    prev.streamStatus === next.streamStatus &&
    prev.showToolIoExpansion === next.showToolIoExpansion &&
    prev.showStaffFeedback === next.showStaffFeedback &&
    prev.hideConversationTodoToolRows === next.hideConversationTodoToolRows &&
    sameHostedTodoEpochs(prev.hostedTodoEpochs, next.hostedTodoEpochs)
);
AgentMessage.displayName = "AgentMessage";

/**
 * Renders the Scout message markdown to sanitized HTML for the clipboard.
 *
 * The HTML representation is written alongside the markdown source so paste
 * targets that prefer rich text (Word, Google Docs, Slack, Notion) render
 * formatted output, while markdown-aware tools still receive the raw source
 * via `text/plain`. Sanitization is defense-in-depth — paste targets generally
 * sanitize too, but the model can emit raw inline HTML in its response.
 */
function renderScoutMessageHtml(markdown: string): string {
  if (!markdown) {
    return "";
  }
  const html = marked.parse(markdown, {
    async: false,
    gfm: true,
    breaks: true,
  });
  return DOMPurify.sanitize(html);
}

function AgentMessageToolbar({ message, activeTurn, streamStatus }: AgentMessageProps) {
  const copyToClipboard = useCopyToClipboard();
  const messageText = useMemo(() => getScoutMessageText(message).trim(), [message]);
  const messageHtml = useMemo(() => renderScoutMessageHtml(messageText), [messageText]);
  const stableMessageLinkId = message.persistedMessageId;
  const messageDeepLinkUrl = useScoutDeepLinkUrl(stableMessageLinkId);
  const terminalError = useMemo(() => getScoutTerminalError(message), [message]);
  const handleCopy = useCallback(async () => {
    await copyToClipboard({
      plainText: messageText,
      html: messageHtml || undefined,
    });
  }, [copyToClipboard, messageHtml, messageText]);
  const handleCopyLink = useCallback(async () => {
    if (!messageDeepLinkUrl) {
      return;
    }

    await copyToClipboard(messageDeepLinkUrl);
  }, [copyToClipboard, messageDeepLinkUrl]);

  if (messageText.length === 0) {
    return null;
  }

  if (terminalError) {
    return null;
  }

  if (shouldSuppressAgentMessageToolbar({ message, activeTurn, streamStatus })) {
    return null;
  }

  return (
    <Stack width="100%" direction="row" alignItems="center" spacing={1} sx={{ ml: -1 }}>
      <Tooltip title="Copy text">
        <IconButton aria-label="Copy text" size="small" onClick={handleCopy}>
          <CopyAllOutlined fontSize="small" />
        </IconButton>
      </Tooltip>
      {messageDeepLinkUrl && (
        <ScoutDeepLinkCopyButton
          ariaLabel="Copy link to message"
          tooltip="Copy link to message"
          onClick={handleCopyLink}
          iconFontSize="small"
        />
      )}
      <FeedbackButtons message={message} />
    </Stack>
  );
}

function formatStaffFeedbackTimestamp(value: string | undefined): string | undefined {
  if (!value) {
    return undefined;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
}

function StaffFeedbackAccordion({
  message,
  showStaffFeedback = false,
}: Pick<AgentMessageProps, "message" | "showStaffFeedback">) {
  const feedback = message.feedback;
  const feedbackText = showStaffFeedback ? (feedback?.feedback?.trim() ?? "") : "";
  const feedbackDeepLinkUrl = useScoutDeepLinkUrl(
    showStaffFeedback && !message.isStreaming ? feedback?.id : undefined
  );
  const isDeepLinkTarget = useScoutIsDeepLinkTarget(feedback?.id);
  const [expanded, setExpanded] = useState(isDeepLinkTarget);
  const copyToClipboard = useCopyToClipboard();
  const handleCopyLink = useCallback(
    async (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      if (!feedbackDeepLinkUrl) {
        return;
      }

      await copyToClipboard(feedbackDeepLinkUrl);
    },
    [copyToClipboard, feedbackDeepLinkUrl]
  );

  useEffect(() => {
    if (isDeepLinkTarget) {
      setExpanded(true);
    }
  }, [isDeepLinkTarget]);

  if (
    !feedback ||
    feedback.rating !== ScoutMessageFeedbackRating.ThumbsDown ||
    feedbackText.length === 0
  ) {
    return null;
  }

  const timestamp = formatStaffFeedbackTimestamp(feedback.createdAt);
  const metadata = [feedback.submittedByEmail, timestamp].filter(Boolean);

  return (
    <Accordion
      id={feedback.id}
      disableGutters
      variant="outlined"
      expanded={expanded}
      onChange={(_event, nextExpanded) => setExpanded(nextExpanded)}
      tabIndex={-1}
      sx={{
        mt: 1,
        bgcolor: "background.paper",
        position: "relative",
        ...getScoutDeepLinkTargetSx(isDeepLinkTarget),
      }}
    >
      <AccordionSummary
        component="div"
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`${message.id}-staff-feedback-content`}
        id={`${message.id}-staff-feedback-header`}
      >
        <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
          Staff feedback
        </Typography>
        {feedbackDeepLinkUrl ? (
          <ScoutDeepLinkCopyButton
            ariaLabel="Copy link to staff feedback"
            tooltip="Copy link to staff feedback"
            onClick={handleCopyLink}
            sx={{ mr: 1, p: 0.25 }}
          />
        ) : null}
      </AccordionSummary>
      <AccordionDetails>
        {metadata.length > 0 ? (
          <Typography variant="caption" color="text.secondary">
            {metadata.join(" • ")}
          </Typography>
        ) : null}
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mt: 0.25 }}>
          {feedback.feedback}
        </Typography>
      </AccordionDetails>
    </Accordion>
  );
}

function ScoutStaffFeedbackDialog({
  open,
  value,
  loading,
  onChange,
  onClose,
  onSubmit,
}: {
  open: boolean;
  value: string;
  loading: boolean;
  onChange: (value: string) => void;
  onClose: () => void;
  onSubmit: () => void;
}) {
  const submitDisabled = loading || value.trim().length === 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      title="Staff feedback"
      description={STAFF_FEEDBACK_PROMPT}
      primaryActionButtonProps={{
        onClick: onSubmit,
        loading,
        disabled: submitDisabled,
        saveText: "Submit",
      }}
    >
      <TextField
        autoFocus
        fullWidth
        multiline
        minRows={4}
        margin="dense"
        placeholder="Feedback"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </Dialog>
  );
}

function FeedbackButtons({ message }: { message: ScoutChatMessage }) {
  const { tenantId } = useParams();
  const { isStaff } = useCurrentUser();
  const { host } = useScoutHostContext();
  const [optimisticRating, setOptimisticRating] = useState<
    ScoutMessageFeedbackRating | null | undefined
  >(undefined);
  const [staffFeedbackDialogOpen, setStaffFeedbackDialogOpen] = useState(false);
  const [staffFeedbackDraft, setStaffFeedbackDraft] = useState("");
  const [rateScoutMessage, { loading }] = useMutation(RATE_SCOUT_MESSAGE);
  const rateableMessageId = useMemo(() => resolveRateableMessageId(message), [message]);

  const currentRating: ScoutMessageFeedbackRating | null =
    optimisticRating !== undefined ? optimisticRating : (message.feedback?.rating ?? null);

  const submitRating = useCallback(
    async (rating: ScoutMessageFeedbackRating, feedbackText?: string): Promise<boolean> => {
      if (!tenantId || !rateableMessageId) {
        return false;
      }

      const previous = currentRating;
      setOptimisticRating(rating);
      const input = feedbackText
        ? { tenantId, messageId: rateableMessageId, rating, feedback: feedbackText, host }
        : { tenantId, messageId: rateableMessageId, rating, host };
      try {
        await rateScoutMessage({ variables: { input } });
        return true;
      } catch {
        setOptimisticRating(previous);
        return false;
      }
    },
    [currentRating, host, rateScoutMessage, rateableMessageId, tenantId]
  );

  const handleThumbsDownClick = useCallback(() => {
    if (isStaff) {
      setStaffFeedbackDraft("");
      setStaffFeedbackDialogOpen(true);
      return;
    }

    void submitRating(ScoutMessageFeedbackRating.ThumbsDown);
  }, [isStaff, submitRating]);

  const handleStaffFeedbackSubmit = useCallback(async () => {
    const trimmedFeedback = staffFeedbackDraft.trim();
    if (trimmedFeedback.length === 0) {
      return;
    }

    const success = await submitRating(ScoutMessageFeedbackRating.ThumbsDown, trimmedFeedback);
    if (success) {
      setStaffFeedbackDialogOpen(false);
      setStaffFeedbackDraft("");
    }
  }, [staffFeedbackDraft, submitRating]);

  const handleStaffFeedbackClose = useCallback(() => {
    if (loading) {
      return;
    }
    setStaffFeedbackDialogOpen(false);
    setStaffFeedbackDraft("");
  }, [loading]);

  const isThumbsUp = currentRating === ScoutMessageFeedbackRating.ThumbsUp;
  const isThumbsDown = currentRating === ScoutMessageFeedbackRating.ThumbsDown;
  const buttonsDisabled = loading || !tenantId || !rateableMessageId;

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={-0.25}>
        <Tooltip title="Good response">
          <IconButton
            aria-label="Good response"
            aria-pressed={isThumbsUp}
            size="small"
            disabled={buttonsDisabled}
            onClick={() => void submitRating(ScoutMessageFeedbackRating.ThumbsUp)}
          >
            {isThumbsUp ? <ThumbUpAlt fontSize="small" /> : <ThumbUpAltOutlined fontSize="small" />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Bad response">
          <IconButton
            aria-label="Bad response"
            aria-pressed={isThumbsDown}
            size="small"
            disabled={buttonsDisabled}
            onClick={handleThumbsDownClick}
          >
            {isThumbsDown ? (
              <ThumbDownAlt fontSize="small" />
            ) : (
              <ThumbDownAltOutlined fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </Stack>
      <ScoutStaffFeedbackDialog
        open={staffFeedbackDialogOpen}
        value={staffFeedbackDraft}
        loading={loading}
        onChange={setStaffFeedbackDraft}
        onClose={handleStaffFeedbackClose}
        onSubmit={handleStaffFeedbackSubmit}
      />
    </>
  );
}
